import ActionButton from "@/components/buttons/ActionButton";
import { SortableItem } from "@/components/sortable/SortableItem";
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import type { DragEndEvent } from "@dnd-kit/core/dist/types";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Card, Empty, Space } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { FieldConstraintViolation } from "../../../../../../common/types";
import { TravelCalcDraft } from "../../../../drafts/types";
import { CalcResultInstitutionsFilter } from "../../../components/calc-result/CalcResultInstitutionsFilter";
import { CalcResultSorting } from "../../../components/calc-result/CalcResultSorting";
import { CalcNavigation } from "../../../components/CalcNavigation";
import { DraftOfferForm } from "../../../components/DraftOfferForm";
import { CalcResultsSorting } from "../../../enums";
import { useInstitutionsFilter } from "../../../hooks";
import { OperationStage } from "../../../realty/enums";
import { CalcResult } from "../../../types";
import { moveActiveItemsFirst, sortCalcResultItems } from "../../../utils";
import { useTravelCalcResults } from "../../contexts";
import type { TravelCalc, TravelCalcResultData } from "../../types";
import { TravelCalcResult } from "./travel-calc-result/TravelCalcResult";
import { TravelSaveDraftButton } from "./TravelSaveDraftButton";

export type Props = {
  calcData: TravelCalc;
  handleGenerateContract: (selectedItem: CalcResult<TravelCalcResultData>) => void;
  handleShowErrors: (errors: FieldConstraintViolation[]) => void;
  handleGoToPreviousStep: () => void;
  draft?: TravelCalcDraft;
};

export const TravelCalcResultsView = ({
  calcData,
  handleGenerateContract,
  handleShowErrors,
  handleGoToPreviousStep,
  draft,
}: Props) => {
  const { t } = useTranslation();

  const { applicableResults: resultItems } = useTravelCalcResults();

  const [draftOfferForm, setDraftOfferForm] = useState<"createOffer" | "createUpdateDraft" | "sendOffer">();
  const [isUpdateDraftOffer, setIsUpdateDraftOffer] = useState(false);

  const [resultsSorting, setResultsSorting] = useState<CalcResultsSorting>(CalcResultsSorting.INSITUTION);
  const [calcResultItems, setCalcResultItems] = useState<CalcResult<TravelCalcResultData>[]>(() =>
    sortCalcResultItems({
      sortBy: resultsSorting,
      items: resultItems,
    }),
  );

  const { excludedInstitutionIds, setExcludedInstitutionIds, visibleResultItems } =
    useInstitutionsFilter(calcResultItems);

  useEffect(() => {
    setCalcResultItems(
      sortCalcResultItems({
        sortBy: resultsSorting,
        items: resultItems,
      }),
    );
  }, [resultItems]);

  const handleSaveDraft = (isUpdate: boolean) => {
    setIsUpdateDraftOffer(isUpdate);
    setDraftOfferForm("createUpdateDraft");
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) {
      return;
    }

    const activeIndex = calcResultItems.findIndex((item) => item.id === active.id);
    const overIndex = calcResultItems.findIndex((item) => item.id === over.id);

    if (activeIndex !== overIndex) {
      setCalcResultItems((prev) => arrayMove(prev, activeIndex, overIndex));
    }
  };

  const handleItemActivation = (id: string, activation: boolean) => {
    setCalcResultItems((prev) => {
      const updatedItems = prev.map((item) => (item.id === id ? { ...item, active: activation } : item));

      return prev.some((item) => item.id === id && item.active !== activation)
        ? moveActiveItemsFirst(updatedItems)
        : updatedItems;
    });
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  return (
    <div>
      <Card
        title={<h2>{t("calc.titles.insuranceOffer")}</h2>}
        className="card-box"
        extra={
          <Space>
            <TravelSaveDraftButton draft={draft} handleSaveDraft={handleSaveDraft} />

            <ActionButton
              icon="download"
              color="green"
              variant="outlined"
              size="middle"
              disabled={!visibleResultItems.length}
              onClick={() => setDraftOfferForm("createOffer")}
            >
              {t("calc.actions.generateOffer")}
            </ActionButton>

            <ActionButton
              icon="mail"
              color="green"
              variant="outlined"
              size="middle"
              disabled={!visibleResultItems.length}
              onClick={() => setDraftOfferForm("sendOffer")}
            >
              {t("calc.actions.sendOffer")}
            </ActionButton>
          </Space>
        }
      >
        <CalcResultInstitutionsFilter
          results={resultItems}
          excludedInstitutionIds={excludedInstitutionIds}
          onChange={setExcludedInstitutionIds}
        />
      </Card>

      <Card
        className="card-box"
        title={
          <h2 style={{ fontSize: 20 }}>
            {t("calc.travel.titles.insuranceType." + calcData.generalData.insuranceType)}
          </h2>
        }
        extra={
          <CalcResultSorting
            resultItems={resultItems}
            resultsSorting={resultsSorting}
            onChange={(sorting, results) => {
              setResultsSorting(sorting);
              setCalcResultItems(results);
            }}
          />
        }
      >
        {visibleResultItems.length ? (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={visibleResultItems} strategy={verticalListSortingStrategy}>
              {visibleResultItems.map((item) => (
                <SortableItem key={item.id} id={item.id} error={item.error} disabled={!item.active}>
                  <TravelCalcResult
                    item={item}
                    handleItemActivation={handleItemActivation}
                    handleGenerateContract={handleGenerateContract}
                    handleShowErrors={handleShowErrors}
                    handleItemRecommendation={(itemId: string, activation: boolean) =>
                      setCalcResultItems((prev) =>
                        prev.map((item) => (item.id === itemId ? { ...item, recommended: activation } : item)),
                      )
                    }
                  />
                </SortableItem>
              ))}
            </SortableContext>
          </DndContext>
        ) : (
          <Empty description={t("calc.helpers.noDataByFilter")} />
        )}
      </Card>

      <DraftOfferForm
        isUpdate={isUpdateDraftOffer}
        formType={draftOfferForm}
        calcData={calcData}
        calcResults={visibleResultItems.filter((item) => item.active && !item.error)}
        operationStage={OperationStage.CALCULATE}
        draft={draft}
        onFinish={() => setDraftOfferForm(undefined)}
      />

      <CalcNavigation onGoToPrevious={handleGoToPreviousStep} />
    </div>
  );
};
